import type { ReactNode } from 'react'
import { VisuallyHidden } from '@n26/nxd'
import type { Figure } from '@/features/dashboard/format/figure'
import { valueOptions } from '@/features/dashboard/format/figure'
import {
  formatValue,
  formatValueParts,
  trendFromChange,
} from '@/features/dashboard/format/format'
import { DeltaBadge } from './DeltaBadge'
import { Odometer, zeroFrom } from '../charts/Odometer'
import styles from './CardFigure.module.css'

type CardFigureProps = {
  figure: Figure
  /** tile: the 32px figure on a tile; expanded: the 64px one in the panel. */
  size: 'tile' | 'expanded'
  className?: string | undefined
  /** Shown under the change. */
  children?: ReactNode
}

/** 'YTD YoY' reads 'YTD • YoY', as the design has it. */
const changeLabel = (label: string) => label.replace(/^YTD /, 'YTD • ')

export const CardFigure = ({
  figure,
  size,
  className,
  children,
}: CardFigureProps) => {
  const options = valueOptions(figure)
  const { prefix, main, suffix, unit } = formatValueParts(figure.value, options)
  // The figure's digits roll from the previous period's value
  // (from zeros without one), up if it rose and down if it fell.
  const from =
    figure.previous === null || figure.previous === 0
      ? zeroFrom(main)
      : formatValueParts(figure.previous, options).main
  const direction =
    figure.previous !== null && figure.value < figure.previous ? 'down' : 'up'
  return (
    <div className={`${styles.values} ${styles[size]} ${className ?? ''}`}>
      <span className={styles.figure}>
        <VisuallyHidden>{formatValue(figure.value, options)}</VisuallyHidden>
        <span className={styles.figureParts} aria-hidden='true'>
          {prefix}
          <Odometer
            value={main}
            from={from}
            direction={direction}
            inverted={figure.lowerIsBetter}
          />
          {suffix && (
            <Odometer
              className={styles.suffix}
              value={suffix}
              from={suffix}
              direction={direction}
              inverted={figure.lowerIsBetter}
            />
          )}
          {unit && <span className={styles.suffix}>{unit}</span>}
        </span>
      </span>
      {figure.change !== null && (
        <span className={styles.change}>
          <DeltaBadge
            change={figure.change}
            unit={figure.changeUnit}
            value={options}
            lowerIsBetter={figure.lowerIsBetter}
            direction={trendFromChange(figure.change)}
            size={size === 'tile' ? 'small' : 'large'}
          />
          {figure.label && (
            <span className={styles.label}>{changeLabel(figure.label)}</span>
          )}
        </span>
      )}
      {children}
    </div>
  )
}
